import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Switch,
  Textarea,
} from "@eduviaos/ui-kit";
import {
  ApiError,
  SLUG_RULES,
  boundedNameSchema,
  deriveSlug,
  fieldError,
  slugProblemFor,
} from "@eduviaos/shared";
import { api } from "@/lib/api";

/**
 * Phase B2.6c — "+ New custom form" CTA on the tenant detail Forms tab.
 *
 * Creates a from-scratch form (sister of the clone-from-canonical path).
 * On success, navigates straight to the editor so the SA can build out
 * the field list. Server returns the created `FormResponse` so we can
 * pluck `slug` for the deep-link.
 *
 * Wires:
 *   - POST /super-admin/tenants/{slug}/forms
 *
 * The form starts with one default field (a single-line "title" text
 * input). Without a starter field the editor opens empty and the SA
 * has to add one immediately to satisfy `validate_form_structure`;
 * pre-seeding mirrors how cloned forms always arrive with fields.
 *
 * The slug is derived from the display name and never shown; slug
 * problems (empty, colliding) are reported on the name field.
 */

const FORM_NAME_MAX = 160;
const SHORT_TEXT_MAX = 80;
const SUCCESS_MESSAGE_MAX = 500;
const formNameSchema = boundedNameSchema(FORM_NAME_MAX, "Form name");
const submitLabelSchema = boundedNameSchema(SHORT_TEXT_MAX, "Submit button label");

interface FormCreateResponse {
  id: string;
  slug: string;
  name: string;
  version: number;
}

interface Props {
  tenantSlug: string;
  /** Slugs already in use for this tenant — used to surface a friendly
   *  conflict message before round-tripping the BE. */
  existingSlugs: ReadonlySet<string>;
}

export function NewFormDialog({ tenantSlug, existingSlugs }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [name, setName] = React.useState("");
  const [purpose, setPurpose] = React.useState("custom");
  const [requiresAuth, setRequiresAuth] = React.useState(true);
  const [successMessage, setSuccessMessage] = React.useState(
    "Thank you. Your response has been recorded.",
  );
  const [submitButtonLabel, setSubmitButtonLabel] = React.useState("Submit");
  const [showErrors, setShowErrors] = React.useState(false);

  const slug = deriveSlug(name, SLUG_RULES.formSlug);
  const nameError =
    fieldError(formNameSchema, name) ??
    slugProblemFor(slug, SLUG_RULES.formSlug, { noun: "form", taken: existingSlugs });
  const submitLabelError = fieldError(submitLabelSchema, submitButtonLabel);
  const visibleNameError = showErrors ? nameError : null;
  const visibleSubmitLabelError = showErrors ? submitLabelError : null;

  const reset = () => {
    setName("");
    setPurpose("custom");
    setRequiresAuth(true);
    setSuccessMessage("Thank you. Your response has been recorded.");
    setSubmitButtonLabel("Submit");
    setShowErrors(false);
  };

  const mutation = useMutation<FormCreateResponse, ApiError>({
    mutationFn: () =>
      api.post<FormCreateResponse>(`/super-admin/tenants/${tenantSlug}/forms`, {
        name: name.trim(),
        slug,
        purpose: purpose.trim() || "custom",
        submit_button_label: submitButtonLabel.trim() || "Submit",
        success_message: successMessage.trim(),
        requires_auth: requiresAuth,
        // Seed with one starter field so the editor opens with something
        // visible. The SA will replace / append immediately — this is
        // strictly a placeholder so the form is structurally valid.
        fields: [
          {
            key: "title",
            label: "Title",
            field_type: "text",
            required: true,
            order: 0,
          },
        ],
      }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["super-admin", "tenant-forms", tenantSlug] });
      setOpen(false);
      reset();
      // Land directly in the editor — that's the whole point of this CTA.
      navigate(`/tenants/${tenantSlug}/forms/${created.slug}/edit`);
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowErrors(true);
    if (nameError || submitLabelError) return;
    mutation.mutate();
  };

  const serverConflict = mutation.error instanceof ApiError && mutation.error.status === 409;
  const serverError =
    mutation.error instanceof ApiError && !serverConflict ? mutation.error.message : null;
  const nameMessage =
    visibleNameError ??
    (serverConflict ? "Another form already has this name. Choose a different name." : null);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          mutation.reset();
          reset();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="default" data-testid="new-form-trigger">
          <Plus className="size-3.5" aria-hidden="true" />
          New custom form
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create custom form</DialogTitle>
          <DialogDescription>
            Builds a brand-new tenant-scoped form from scratch (no canonical template). After
            create, you'll land in the editor to add fields. Form starts at v1.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} noValidate className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="new-form-name">Display name</Label>
            <Input
              id="new-form-name"
              required
              autoFocus
              maxLength={FORM_NAME_MAX}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (serverConflict) mutation.reset();
              }}
              onBlur={() => setShowErrors(true)}
              placeholder="e.g. Parent feedback survey"
              aria-invalid={nameMessage ? true : undefined}
              aria-describedby={nameMessage ? "new-form-name-error" : undefined}
            />
            {nameMessage ? (
              <p id="new-form-name-error" className="text-destructive text-xs">
                {nameMessage}
              </p>
            ) : null}
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="new-form-purpose">Purpose</Label>
            <Input
              id="new-form-purpose"
              maxLength={SHORT_TEXT_MAX}
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="custom"
              aria-describedby="new-form-purpose-help"
            />
            <p id="new-form-purpose-help" className="text-muted-foreground text-xs">
              Free-form tag the tenant uses to group their forms (e.g. <code>survey</code>,{" "}
              <code>feedback</code>, <code>internal</code>). Unrelated to the admission stage link —
              that's set from the Admissions tab.
            </p>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="new-form-submit-label">Submit button label</Label>
            <Input
              id="new-form-submit-label"
              maxLength={SHORT_TEXT_MAX}
              value={submitButtonLabel}
              onChange={(e) => setSubmitButtonLabel(e.target.value)}
              aria-invalid={visibleSubmitLabelError ? true : undefined}
              aria-describedby={visibleSubmitLabelError ? "new-form-submit-label-error" : undefined}
            />
            {visibleSubmitLabelError ? (
              <p id="new-form-submit-label-error" className="text-destructive text-xs">
                {visibleSubmitLabelError}
              </p>
            ) : null}
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="new-form-success">Success message</Label>
            <Textarea
              id="new-form-success"
              rows={2}
              maxLength={SUCCESS_MESSAGE_MAX}
              value={successMessage}
              onChange={(e) => setSuccessMessage(e.target.value)}
            />
          </div>
          <label className="border-border bg-muted/30 flex cursor-pointer items-center justify-between gap-3 rounded-md border px-3 py-2">
            <div className="grid gap-0.5">
              <span className="text-sm font-medium">Require authentication</span>
              <span className="text-muted-foreground text-xs">
                When ON, only signed-in users can submit. When OFF, anonymous submissions are
                accepted (tenant resolved via X-Tenant-Slug header).
              </span>
            </div>
            <Switch checked={requiresAuth} onCheckedChange={(v) => setRequiresAuth(!!v)} />
          </label>
          {serverError && (
            <p
              role="alert"
              className="text-destructive border-destructive/50 bg-destructive/5 rounded-md border px-3 py-2 text-sm"
            >
              {serverError}
            </p>
          )}
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                  Creating…
                </>
              ) : (
                "Create form"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
